class HeaderReveal {

    constructor() {

        this.header = null;
        this.headerContent = null;
        this.main = null;

        this.sections = [];
        this.surfaces = [];

        this.canvas = document.createElement("canvas");

        this.ctx = this.canvas.getContext("2d", {
            willReadFrequently: true
        });

        this.sampleWidth = 32;
        this.sampleHeight = 12;

        this.currentTheme = null;

        this.ticking = false;

        this.debug = false;

        this.dynamicColor = false;

    }

    init() {

        this.header = document.querySelector(".header-wrapper");
        this.primaryLayer = this.header.querySelector(".header-layer--primary");
        this.secondaryLayer = this.header.querySelector(".header-layer--secondary");

        this.dynamicColor = this.header.dataset.dynamicColor === "true";

        this.createMask();

        if (!this.header) return;

        this.headerContent = this.header.querySelector(".header");
        this.main = document.querySelector("#MainContent");

        if (!this.main) return;

        this.sections = Array.from(this.main.querySelectorAll(":scope > .shopify-section"));
        // document.querySelectorAll(":scope > .shopify-section")

        window.addEventListener("scroll", () => {

            if (this.ticking) return;

            this.ticking = true;

            requestAnimationFrame(() => {

                this.update();
                this.ticking = false;

            });

        }, { passive: true });

        window.addEventListener("resize", () => this.update());

        this.update();

    }

    update() {

        this.updateScrollState();

        this.surfaces = this.getSurfaces();

        const visibleSurfaces = this.getVisibleSurfaces();

        if (!visibleSurfaces.length) {
            return;
        }

        for (const surface of visibleSurfaces){

            this.analyzeSurface(surface);

        }

        this.renderReveal(
            visibleSurfaces
        );

    }

    updateScrollState() {

        if (!this.header) return;

        this.header.classList.toggle("scroll-active", window.scrollY > 0);

        // #main-collection-filters (component-facets.css) se posiciona
        // sticky justo debajo del header con un top fijo (90px/99px) que no
        // coincide siempre con la altura real (varia con la announcement
        // bar, breakpoints, etc.). Con el header transparente ese desajuste
        // se ve: solape en unos anchos, hueco fino en otros. Exponemos la
        // altura real para que component-facets.css la use en vez del valor
        // fijo (con ese mismo valor como fallback si esto no corre).
        document.documentElement.style.setProperty(
            "--header-total-height",
            `${this.header.offsetHeight}px`
        );

    }

    getVisibleSurfaces() {

        if (!this.surfaces.length) {
            return [];
        }

        const headerRect = this.headerContent.getBoundingClientRect();

        const visibleSurfaces = [];

        for (const surface of this.surfaces) {

            let rect;

            if (
                surface.type === "padding" &&
                surface.top !== undefined &&
                surface.bottom !== undefined
            ) {
                
                rect = {
                    top: surface.top,
                    bottom: surface.bottom
                };

            } else {
                rect = surface.section.getBoundingClientRect();
            }

            if (rect.bottom <= headerRect.top || rect.top >= headerRect.bottom) {
                continue;
            }

            visibleSurfaces.push({...surface, rect});

        }

        return visibleSurfaces;

    }

    getSurfaces() {

        const surfaces = [];
        const sections = this.sections;
        sections.forEach((section, index) => {
            surfaces.push(...this.buildSurfaces(section, index));
        });

        return surfaces;

    }

    buildSurfaces(section, index) {

        const surfaces = [];

        const visualSurface =this.buildVisualSurface(section);

        if (visualSurface) {
            surfaces.push(visualSurface);
        }

        const paddingSurface =this.buildPaddingSurface(section, index);

        if (paddingSurface) {
            surfaces.push(paddingSurface);
        }

        return surfaces;

    }

    buildVisualSurface(section) {

        const element = this.findSurfaceElement(section);

        if (element) {
            return {
                section,
                type: "visual", 
                element,
                manualTheme: section.dataset.headerTheme || "auto",
                theme: null
            };
        }
        /*
        * Surface padding
        */
        if (section.querySelector(".space-section") || section.id.includes("__margin_")) {

            return {

                section,
                type: "padding",
                element: null,
                manualTheme: section.dataset.headerTheme || "auto",
                theme: "primary"

            };

        }

        /*
        * Surface sólida
        * (placeholder para futuras superficies)
        */
        return {
            section,
            type: "solid",
            element: null,
            manualTheme: section.dataset.headerTheme || "auto",
            theme: null
        };

    }

    buildPaddingSurface(section, index){
        const nextSection = this.sections[index + 1];

        if (!nextSection) {
            return null;
        }

        const currentRect = section.getBoundingClientRect();

        const nextRect = nextSection.getBoundingClientRect();

        if (nextRect.top <= currentRect.bottom) {
            return null;
        }

        return {
            type: "padding",
            section,
            nextSection,
            top: currentRect.bottom,
            bottom: nextRect.top,
            manualTheme: "auto",
            theme: null
        };
    }

    analyzeSurface(surface) {

        if (!this.dynamicColor) {

            surface.theme = "primary";

            return;

        }

        switch (surface.type) {

            case "visual":

                this.analyzeVisualSurface(surface);

                break;

            case "padding":

                this.analyzePaddingSurface(surface);

                break;

            default:

                console.warn("Unknown surface:", surface.type);

        }

    }

    findSurfaceElement(section) {

        const selectors = [

            "video",
            "picture img",
            "img"

        ];

        for (const selector of selectors) {

            const visual = section.querySelector(selector);

            if (visual) {

                return visual;

            }

        }

        return null;

    }

    analyzeVisualSurface(surface) {

        if (surface.manualTheme !== "auto") {

            surface.theme = surface.manualTheme;

            this.emit(surface);

            return;

        }

        if (!surface.element) {

            surface.theme = "primary";

            this.emit(surface);

            return;

        }

        const visual = surface.element;

        if (
            visual instanceof HTMLVideoElement &&
            visual.readyState < HTMLMediaElement.HAVE_CURRENT_DATA
        ) {

            visual.addEventListener(
                "loadeddata",
                () => {

                    this.analyzeVisualSurface(surface);

                },
                {
                    once: true
                }
            );

            return;

        }

        const imageData =this.captureVisual(visual);

        if (!imageData) return;

        const rgb =this.calculateAverageColor(imageData);
        surface.theme =this.calculateContrastTheme(rgb);

        this.emit(surface);

    }

    analyzePaddingSurface(surface){

        const nextVisual =

            this.surfaces.find(candidate =>

                candidate.section ===
                surface.nextSection &&

                candidate.type === "visual"

            );

        if(!nextVisual){
            return;
        }

        if (this.debug) {
            console.log({
                padding: surface.section.id,
                next: nextVisual?.section.id,
                nextTheme: nextVisual?.theme
            });
        }

        this.analyzeVisualSurface(nextVisual);

        surface.theme = nextVisual.theme;

    }

    captureVisual(visual) {

        this.canvas.width = this.sampleWidth;
        this.canvas.height = this.sampleHeight;

        this.ctx.clearRect(0, 0, this.sampleWidth, this.sampleHeight);
        this.ctx.drawImage(visual, 0, 0, this.sampleWidth, this.sampleHeight);

        return this.ctx.getImageData(0, 0, this.sampleWidth, this.sampleHeight);

    }

    srgbToLinear(value) {

        const c = value / 255;

        return c <= 0.04045
            ? c / 12.92
            : Math.pow((c + 0.055) / 1.055, 2.4);

    }

    calculateAverageColor(imageData) {

        // Promediamos en espacio lineal (no en sRGB, que es el espacio
        // "gamma" en el que ya vienen codificados los valores del canvas).
        // Promediar directamente los valores sRGB subestima el brillo
        // percibido de superficies con mucho contraste interno (mosaicos
        // de muchas imagenes pequeñas: blanco puro de fondo mezclado con
        // sombras/zonas oscuras de cada foto). Ej: 50% blanco + 50% negro
        // promediado en sRGB da 128 (justo el umbral), pero la luz
        // percibida de esa mezcla equivale a un gris mucho mas claro. Sin
        // esta correccion, ese tipo de fondo se clasifica como "oscuro" y
        // el header pinta su contraste en blanco, camuflandose contra un
        // fondo que en realidad se ve blanco/claro.
        let r = 0;
        let g = 0;
        let b = 0;

        const data = imageData.data;

        const pixels = data.length / 4;

        for (let i = 0; i < data.length; i += 4) {

            r += this.srgbToLinear(data[i]);
            g += this.srgbToLinear(data[i + 1]);
            b += this.srgbToLinear(data[i + 2]);

        }

        return {

            r: r / pixels,
            g: g / pixels,
            b: b / pixels

        };

    }

    calculateContrastTheme(rgb) {

        // Luminancia relativa (Rec. 709) sobre el promedio lineal.
        const luminance =
            0.2126 * rgb.r +
            0.7152 * rgb.g +
            0.0722 * rgb.b;

        // Umbral equivalente al antiguo "brightness > 128" pero expresado
        // en espacio lineal (srgbToLinear(128)), para no cambiar el
        // resultado en superficies de color uniforme y solo corregir el
        // caso de superficies con mezcla de tonos.
        return luminance > this.srgbToLinear(128)
            ? "primary"
            : "secondary";

    }

    emit(surface) {

        if (surface.theme === this.currentTheme) {
            return;
        }

        this.currentTheme = surface.theme;
        this.setTheme(surface.theme);

    }

    setTheme(theme) {

        if (!this.secondaryLayer) return;

        this.secondaryLayer.dataset.theme = theme;

    }

    renderReveal(visibleSurfaces) {

        const headerRect = this.headerContent.getBoundingClientRect();

        for (const surface of visibleSurfaces) {

            surface.visibleTop = Math.max(surface.rect.top, headerRect.top);
            surface.visibleBottom = Math.min(surface.rect.bottom, headerRect.bottom);
            surface.visibleHeight = surface.visibleBottom - surface.visibleTop;

        }

        const bands = visibleSurfaces
            .map(surface => ({
                theme: surface.theme,
                top: surface.visibleTop,
                bottom: surface.visibleBottom,
                height: surface.visibleHeight
            }))
            .sort((a, b) => a.top - b.top);

        this.renderBands(bands, headerRect);

    }

    renderBands(bands, headerRect) {

        if (this.debug) {
            console.clear();
            console.table(bands);
        }

        this.updateRevealState(bands);

        const secondaryBands = bands.filter(
            band => band.theme === "secondary"
        );

        if (!secondaryBands.length) {

            this.updateMask([]);

            return;

        }

        const windows = this.buildWindows(
            secondaryBands,
            headerRect
        );

        if (this.debug) {
            console.table(windows);
        }

        this.updateMask(windows);

    }

    getUniformTheme(bands) {

        if (!bands.length) return null;

        const first = bands[0].theme;

        const isUniform = bands.every(
            band => band.theme === first
        );

        return isUniform ? first : null;

    }

    updateRevealState(bands) {

        if (!this.header) return;

        const uniformTheme = this.getUniformTheme(bands);

        this.header.dataset.revealState = uniformTheme
            ? `uniform-${uniformTheme}`
            : "mixed";

    }

    buildWindows(secondaryBands, headerRect) {

        const windows = [];

        for (const band of secondaryBands) {

            windows.push({
                top: band.top - headerRect.top,
                bottom: band.bottom - headerRect.top
            });

        }

        return windows;

    }

    createMask() {

        const svgNS = "http://www.w3.org/2000/svg";

        const svg = document.createElementNS(svgNS, "svg");

        svg.setAttribute("width", "0");
        svg.setAttribute("height", "0");
        svg.setAttribute("aria-hidden", "true");

        const defs = document.createElementNS(svgNS, "defs");

        const mask = document.createElementNS(svgNS, "mask");

        mask.id = "header-reveal-mask";

        // Fondo negro = oculto
        const background = document.createElementNS(svgNS, "rect");

        background.setAttribute("x", 0);
        background.setAttribute("y", 0);
        background.setAttribute("fill", "black");

        mask.setAttribute("maskUnits", "userSpaceOnUse");
        mask.setAttribute("maskContentUnits", "userSpaceOnUse");
        mask.appendChild(background);

        defs.appendChild(mask);

        svg.appendChild(defs);

        document.body.appendChild(svg);

        this.mask = mask;

    }

    updateMask(windows) {

        // El sistema de coordenadas de la máscara debe coincidir
        // exactamente con el del header.
        const headerRect = this.headerContent.getBoundingClientRect();

        this.mask.setAttribute("x", 0);
        this.mask.setAttribute("y", 0);
        this.mask.setAttribute("width", headerRect.width);
        this.mask.setAttribute("height", headerRect.height);

        // Eliminamos todas las ventanas anteriores
        this.mask.querySelectorAll(".header-window").forEach(rect => rect.remove());

        const svgNS = "http://www.w3.org/2000/svg";

        for (const window of windows) {

            const rect = document.createElementNS(svgNS, "rect");

            rect.classList.add("header-window");

            rect.setAttribute("x", 0);
            rect.setAttribute("y", window.top);
            rect.setAttribute("width", headerRect.width); // Ya NO usamos porcentajes
            rect.setAttribute("height", window.bottom - window.top);
            rect.setAttribute("fill", "white");

            this.mask.appendChild(rect);

        }

    }

}

document.addEventListener("click", event => {

    const proxy = event.target.closest(".header__menu-proxy");

    if (!proxy) return;

    document
        .querySelector(".header-layer--primary #Details-menu-drawer-container summary")
        ?.click();

});

document.addEventListener("DOMContentLoaded", () => {

    new HeaderReveal().init();

});
